import { useCallback, useMemo, useState } from 'react'
import { Platform } from 'react-native'
import Purchases, { PurchasesPackage } from 'react-native-purchases'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import type { PurchaseOfferingsStatus } from '../utils/purchaseScreenViewState'
import type { PurchaseCurrencyCode } from '../utils/webBillingOfferings.types'
import { queryKeys } from '../../../queries/query-keys'
import { captureException, captureMessage } from '../../../utils/sentry'
import { getWebBillingPackagesByCurrency } from '../utils/webBillingOfferings'
import {
  WEB_BILLING_EURO_OFFERING_IDS,
  WEB_BILLING_SWEDISH_OFFERING_IDS,
} from '../utils/webBillingOfferings.types'

interface UsePurchaseOfferingsParams {
  revenueCatUserId: string | null
  language: string
}

interface UsePurchaseOfferingsResult {
  packages: PurchasesPackage[]
  status: PurchaseOfferingsStatus
  selectedPackage: PurchasesPackage | null
  setSelectedPackage: (pkg: PurchasesPackage | null) => void
  refetchPackages: () => Promise<unknown>
  isRefetchingPackages: boolean
  isSwedish: boolean
  targetCurrency: PurchaseCurrencyCode
}

export interface PurchaseOfferingsLocaleState {
  isSwedish: boolean
  targetCurrency: PurchaseCurrencyCode
}

const PURCHASE_OFFERINGS_MAX_RETRIES = 4
const PURCHASE_OFFERINGS_RETRY_DELAY_MS = 750

const isSwedishLanguage = (language: string): boolean => {
  const normalizedLanguage = language.toLowerCase()
  return normalizedLanguage === 'sv' || normalizedLanguage.startsWith('sv-')
}

export function getPurchaseOfferingsLocaleState(
  language: string,
): PurchaseOfferingsLocaleState {
  const isSwedish = isSwedishLanguage(language)

  return {
    isSwedish,
    targetCurrency: isSwedish ? 'SEK' : 'EUR',
  }
}

type NativeOfferings = Awaited<ReturnType<typeof Purchases.getOfferings>>

function getNativePackagesFromOfferings(
  offerings: NativeOfferings,
): PurchasesPackage[] {
  const currentPackages = offerings.current?.availablePackages ?? []
  if (currentPackages.length > 0) {
    return currentPackages
  }

  const fallbackPackages = Object.values(offerings.all ?? {})
    .filter(
      (offering): offering is NonNullable<(typeof offerings.all)[string]> =>
        offering != null,
    )
    .flatMap((offering) => offering.availablePackages)

  if (fallbackPackages.length > 0) {
    const availableOfferingIds = Object.keys(offerings.all ?? {})
    console.warn(
      'RevenueCat current offering missing packages on native, falling back to all offerings',
      { availableOfferingIds },
    )
    captureMessage('native_purchase_offerings_fallback_to_all', 'warning', {
      feature: 'purchase',
      tags: { flow: 'fetch_offerings', platform: Platform.OS },
      extra: { availableOfferingIds },
    })
  } else {
    captureMessage('native_purchase_offerings_empty', 'warning', {
      feature: 'purchase',
      tags: { flow: 'fetch_offerings', platform: Platform.OS },
      extra: {
        availableOfferingIds: Object.keys(offerings.all ?? {}),
        currentOfferingIdentifier: offerings.current?.identifier ?? null,
      },
    })
  }

  return fallbackPackages.filter(
    (pkg, index, allPackages) =>
      allPackages.findIndex(
        (candidate) => candidate.identifier === pkg.identifier,
      ) === index,
  )
}

async function fetchPurchaseOfferings({
  isWebBilling,
  isSwedish,
  targetCurrency,
}: {
  isWebBilling: boolean
  isSwedish: boolean
  targetCurrency: PurchaseCurrencyCode
}): Promise<PurchasesPackage[]> {
  try {
    if (isWebBilling) {
      const preferredOfferingIds = isSwedish
        ? WEB_BILLING_SWEDISH_OFFERING_IDS
        : WEB_BILLING_EURO_OFFERING_IDS
      const webBillingResult = await getWebBillingPackagesByCurrency(
        targetCurrency,
        preferredOfferingIds,
      )

      if (webBillingResult.packages.length === 0) {
        console.warn(
          'No web billing package found for target currency',
          targetCurrency,
          {
            preferredOfferingIds,
            availableOfferingIds: webBillingResult.availableOfferingIds,
            currentOfferingIdentifier:
              webBillingResult.currentOfferingIdentifier,
            offeringPackageCounts: webBillingResult.offeringPackageCounts,
            availableCurrencies: webBillingResult.availableCurrencies,
          },
        )
        captureMessage('web_purchase_offerings_empty', 'warning', {
          feature: 'purchase',
          tags: { flow: 'fetch_offerings', platform: Platform.OS },
          extra: {
            targetCurrency,
            preferredOfferingIds,
            availableOfferingIds: webBillingResult.availableOfferingIds,
            currentOfferingIdentifier:
              webBillingResult.currentOfferingIdentifier,
            offeringPackageCounts: webBillingResult.offeringPackageCounts,
            availableCurrencies: webBillingResult.availableCurrencies,
          },
        })
      } else if (webBillingResult.usedCurrencyFallback) {
        console.warn(
          'Web billing offerings missing target currency, falling back to first available packages',
          {
            targetCurrency,
            resolvedCurrency: webBillingResult.resolvedCurrency,
            preferredOfferingIds,
            availableOfferingIds: webBillingResult.availableOfferingIds,
            currentOfferingIdentifier:
              webBillingResult.currentOfferingIdentifier,
            offeringPackageCounts: webBillingResult.offeringPackageCounts,
            availableCurrencies: webBillingResult.availableCurrencies,
          },
        )
        captureMessage('web_purchase_offerings_currency_fallback', 'warning', {
          feature: 'purchase',
          tags: { flow: 'fetch_offerings', platform: Platform.OS },
          extra: {
            targetCurrency,
            resolvedCurrency: webBillingResult.resolvedCurrency,
            preferredOfferingIds,
            availableOfferingIds: webBillingResult.availableOfferingIds,
            currentOfferingIdentifier:
              webBillingResult.currentOfferingIdentifier,
            offeringPackageCounts: webBillingResult.offeringPackageCounts,
            availableCurrencies: webBillingResult.availableCurrencies,
          },
        })
      }

      return webBillingResult.packages
    }

    const offerings = await Purchases.getOfferings()
    return getNativePackagesFromOfferings(offerings)
  } catch (error) {
    captureException(error, {
      feature: 'purchase',
      tags: { flow: 'fetch_offerings' },
    })
    throw error
  }
}

function purchaseOfferingsQueryOptions({
  revenueCatUserId,
  language,
}: UsePurchaseOfferingsParams) {
  const { isSwedish, targetCurrency } =
    getPurchaseOfferingsLocaleState(language)
  const isWebBilling = Platform.OS === 'web'

  return queryOptions({
    queryKey: queryKeys.purchase.offerings(revenueCatUserId, targetCurrency),
    queryFn: () =>
      fetchPurchaseOfferings({
        isWebBilling,
        isSwedish,
        targetCurrency,
      }),
    retry: PURCHASE_OFFERINGS_MAX_RETRIES,
    retryDelay: (failureCount) =>
      Math.min(
        PURCHASE_OFFERINGS_RETRY_DELAY_MS * 2 ** Math.max(failureCount - 1, 0),
        4000,
      ),
    staleTime: 5 * 60 * 1000,
  })
}

export function usePurchaseOfferings({
  revenueCatUserId,
  language,
}: UsePurchaseOfferingsParams): UsePurchaseOfferingsResult {
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null,
  )

  const { isSwedish, targetCurrency } =
    getPurchaseOfferingsLocaleState(language)

  const {
    data: packages,
    refetch,
    isFetching,
  } = useSuspenseQuery(
    purchaseOfferingsQueryOptions({
      revenueCatUserId,
      language,
    }),
  )

  const selectedPackage = useMemo(() => {
    const explicitlySelectedPackage =
      selectedPackageId == null
        ? null
        : (packages.find((pkg) => pkg.identifier === selectedPackageId) ?? null)

    if (explicitlySelectedPackage != null) {
      return explicitlySelectedPackage
    }

    const yearly = packages.find((pkg) => pkg.packageType === 'ANNUAL')
    return yearly ?? packages[0] ?? null
  }, [packages, selectedPackageId])

  const setSelectedPackage = useCallback((pkg: PurchasesPackage | null) => {
    setSelectedPackageId(pkg?.identifier ?? null)
  }, [])

  return {
    packages,
    status: packages.length === 0 ? 'empty' : 'ready',
    selectedPackage,
    setSelectedPackage,
    refetchPackages: refetch,
    isRefetchingPackages: isFetching,
    isSwedish,
    targetCurrency,
  }
}
